import { lazy, Suspense } from 'react';
import { useLocation } from 'react-router';
import { CenteredPageLoadingCarrot } from 'turnip/LoadingCarrot/LoadingCarrot';

// Lazy-loaded components
const PurchaseBundle = lazy(async () => ({
  default: (await import('pages/PurchaseBundle/PurchaseBundle')).PurchaseBundle,
}));
const CreateAccountComplete = lazy(async () => ({
  default: (await import('pages/CreateAccountComplete/CreateAccountComplete'))
    .CreateAccountComplete,
}));
const OnboardingRoutes = lazy(async () => ({
  default: (await import('pages/Onboarding/OnboardingRoutes')).OnboardingRoutes,
}));
const MarketScout = lazy(async () => ({
  default: (await import('pages/MarketScout/MarketScout')).MarketScout,
}));
const KeywordExplorerOrUpsell = lazy(async () => ({
  default: (await import('pages/KeywordExplorer/KeywordExplorer'))
    .KeywordExplorerOrUpsell,
}));
const BillingHome = lazy(async () => ({
  default: (await import('pages/BillingHome/BillingHome')).BillingHome,
}));
const SignUp = lazy(async () => ({
  default: (await import('pages/SignUp/SignUp')).SignUp,
}));
const SmoothUpgrade = lazy(async () => ({
  default: (await import('pages/SmoothUpgrade/SmoothUpgrade')).SmoothUpgrade,
}));
const SubscriptionUpdated = lazy(async () => ({
  default: (await import('pages/SubscriptionUpdated/SubscriptionUpdated'))
    .SubscriptionUpdated,
}));
const MembershipEditor = lazy(async () => ({
  default: (await import('pages/MembershipEditor/MembershipEditor'))
    .MembershipEditor,
}));
const Analytics = lazy(async () => ({
  default: (await import('pages/Analytics/Analytics')).Analytics,
}));
const DomainOverview = lazy(async () => ({
  default: (await import('pages/DomainOverview/DomainOverview')).DomainOverview,
}));
const BacklinkAnalysis = lazy(async () => ({
  default: (await import('pages/BacklinkAnalysis/BacklinkAnalysis'))
    .BacklinkAnalysis,
}));
const ContentProMigration = lazy(async () => ({
  default: (await import('pages/ContentProMigration/ContentProMigration'))
    .ContentProMigration,
}));
const ContentOverview = lazy(async () => ({
  default: (await import('pages/ContentOverview/ContentOverview'))
    .ContentOverview,
}));
const SearchPerformance = lazy(async () => ({
  default: (await import('pages/SearchPerformance/SearchPerformance'))
    .SearchPerformance,
}));
const SiteAuditRoutes = lazy(async () => ({
  default: (await import('pages/SiteAudit/SiteAudit.routes')).SiteAuditRoutes,
}));
const SuperAuthorizedPage = lazy(async () => ({
  default: (await import('pages/Super/SuperAuthorizedPage'))
    .SuperAuthorizedPage,
}));
const SuperBundlesList = lazy(async () => ({
  default: (await import('pages/Super/Bundles/List/List')).List,
}));
const SuperBundlesCreate = lazy(async () => ({
  default: (await import('pages/Super/Bundles/Create/Create')).Create,
}));
const CarrotCrmOptIn = lazy(async () => ({
  default: (await import('pages/CarrotCrmOptIn/CarrotCrmOptIn')).CarrotCrmOptIn,
}));
const TeamMembersRoutes = lazy(async () => ({
  default: (await import('pages/TeamMembers/TeamMembers.routes'))
    .TeamMembersRoutes,
}));
const AcceptInvite = lazy(async () => ({
  default: (await import('pages/TeamMembers/AcceptInvite/AcceptInvite'))
    .AcceptInvite,
}));
const SwitchAccount = lazy(async () => ({
  default: (await import('pages/SwitchAccount/SwitchAccount')).SwitchAccount,
}));
const CallTrackingRoutes = lazy(async () => ({
  default: (await import('pages/CallTracking/CallTracking.routes'))
    .CallTrackingRoutes,
}));

function SuspenseWithFallback({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <Suspense fallback={<CenteredPageLoadingCarrot />} key={location.key}>
      {children}
    </Suspense>
  );
}

export {
  AcceptInvite,
  Analytics,
  BacklinkAnalysis,
  BillingHome,
  CallTrackingRoutes,
  CarrotCrmOptIn,
  ContentOverview,
  ContentProMigration,
  CreateAccountComplete,
  DomainOverview,
  KeywordExplorerOrUpsell,
  MarketScout,
  MembershipEditor,
  OnboardingRoutes,
  PurchaseBundle,
  SearchPerformance,
  SignUp,
  SiteAuditRoutes,
  SmoothUpgrade,
  SubscriptionUpdated,
  SuperAuthorizedPage,
  SuperBundlesCreate,
  SuperBundlesList,
  SuspenseWithFallback,
  SwitchAccount,
  TeamMembersRoutes,
};
